import { Button, DialogContent, Typography, useTheme, List, ListItem, ListItemButton, ListItemText, ListItemIcon, DialogProps, Stack } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { NameIdListItem } from '../models/custom-models';
import IconButtonEx, { IconButtonExProps } from '../components/IconButtonEx'
import BusySpinner from '../components/BusySpinner'
import { useErrorDisplay } from '../components/ErrorDisplay';
import ConditionalDisplay from '../components/ConditionalDisplay';
import TZGravatar from '../components/TZGravatar';
import OnTrue from '../components/OnTrue';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Check } from '@mui/icons-material';
import { IsEmpty } from '../Helpers';
import { ResponsiveDialog, ResponsiveDialogHeader, StickyDialogActions } from './responsive';
import useViewportMode from '../hooks/useViewportMode';

export enum SelectItemPopupMode {
    Default,
    StartOpenNoButton
}

export interface SelectItemPopupProps extends Omit<IconButtonExProps, 'onClick'> {
    multiSelect?: boolean;
    title?: string;
    mode?: 'Default' | 'StartOpenNoButton';
    itemLoader: () => Promise<NameIdListItem[]>;
    onSelectItems: (items: NameIdListItem[]) => void | Promise<void>;
    onCancel?: () => void | Promise<void>;
    icon?: JSX.Element;
    cancelBtnText?: string;
    addBtnText?: string;
    dialogProps?: DialogProps;
}
const SelectItemPopup = ({ multiSelect = true, itemLoader, onSelectItems, dialogProps = null, mode = 'Default', title = "Select", addBtnText = 'Add Selected', cancelBtnText = "Cancel", icon = <MoreHorizIcon />, onCancel = () => { }, ...btnProps }: SelectItemPopupProps) => {
    const [busy, setBusy] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const [items, setItems] = useState<NameIdListItem[]>();
    const [ErrorDisplay, setError] = useErrorDisplay();
    const theme = useTheme();
    const viewport = useViewportMode();

    useEffect(() => {
        if (mode === 'StartOpenNoButton' && !open) {
            handleOpen();
        }
    });

    const handleOpen = async () => {
        try {
            setOpen(true);
            setBusy(true);
            let response = await itemLoader();
            response.forEach(x => x.IsSelected = false);
            setItems(response);
        } catch (error) {
            setError(error, "Failed to load items");
            setOpen(false);
        } finally {
            setBusy(false);
        }
    }

    const handleChangeSelection = (item: NameIdListItem) => {
        if (multiSelect) {
            item.IsSelected = !item.IsSelected;
        }
        else {
            items.forEach(x => x.IsSelected = x.Id === item.Id);
            handleAdd(items);
        }
        setItems([...items]);
    }

    const handleAdd = async (currentItems?: NameIdListItem[]) => {
        try {
            setBusy(true);
            await onSelectItems((currentItems ?? items).filter(x => x.IsSelected));
            setOpen(false);
        } catch (error) {
            setError(error, "Failed to add selected");
        } finally {
            setBusy(false);
        }
    }

    const handleCancel = async () => {
        await onCancel();
        setOpen(false);
    }
    return <>
        <ConditionalDisplay ifTrue={mode === 'Default'}>
            <IconButtonEx disabled={busy} onClick={handleOpen} {...btnProps}>{icon}</IconButtonEx>
        </ConditionalDisplay>
        <ErrorDisplay />
        <BusySpinner busy={busy}>
            <ResponsiveDialog aria-labelledby="addusers-dialog-title" open={open} fullWidth maxWidth="sm" tabletFullScreen="portrait" {...dialogProps}>
                <ResponsiveDialogHeader id="addusers-dialog-title" title={title} onClose={handleCancel} />
                <DialogContent dividers sx={{ p: 0, minHeight: 0, overflowY: 'auto' }}>
                    {items?.length > 0 ? (
                        <List dense={viewport.isDesktop} aria-label={title}>
                            {items?.map((i) => (
                                <ListItem key={i.Id} dense={viewport.isDesktop} disablePadding={!viewport.isDesktop}>
                                    <ListItemIcon>
                                        <ConditionalDisplay ifTrue={i.IsSelected}>
                                            <Check fontSize='small' htmlColor={theme.palette.secondary.main} />
                                        </ConditionalDisplay>
                                    </ListItemIcon>
                                    <ListItemButton
                                        onClick={() => handleChangeSelection(i)}
                                        dense={viewport.isDesktop}
                                        selected={i.IsSelected}
                                        aria-selected={i.IsSelected}
                                        sx={{ minHeight: 44 }}
                                    >
                                        <Stack direction='row' spacing={1}>
                                            {IsEmpty(i['Email']) ? null : (
                                                <TZGravatar email={i['Email']} />
                                            )}
                                            <ListItemText primary={i.Name} sx={{marginTop: IsEmpty(i['Email']) ? '0px' : '5px'}} />
                                        </Stack>
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        <Typography sx={{ p: 2 }}>No Items to select</Typography>
                    )}
                </DialogContent>
                <StickyDialogActions phoneLayout="stacked" sx={{ margin: 0, padding: theme.spacing(1) }}>
                    <OnTrue test={multiSelect}><Button disabled={busy || !(items?.some(x => x.IsSelected) ?? false)} onClick={() => handleAdd()}>{addBtnText}</Button></OnTrue>
                    <Button disabled={busy} onClick={handleCancel} color='primary'>{cancelBtnText}</Button>
                </StickyDialogActions>
            </ResponsiveDialog>
        </BusySpinner>
    </>;
}

export default SelectItemPopup
